import { Button, Card, Icon, Modal, Text } from '@/components'
import useViewModel, { OpenModal } from './useViewModel'

// Matches the thumbs in the Plug Sessions feedback banner: no button chrome,
// grey-900 icons on the purple tint
const thumbClass =
  'bg-transparent border-0 p-0 cursor-pointer text-themed-grey-900 flex items-center'

type ViewProps = {
  openModal: OpenModal
  storeLabel: string
  supportEmail: string
  onThumbsUp: () => void
  onThumbsDown: () => void
  onDismiss: () => void
  onCloseModal: () => void
  onOpenStore: () => void
  onEmailSupport: () => void
}

/**
 * Presentational half, exported for Storybook and tests: the banner row plus
 * whichever modal a thumb opened.
 */
export function ReviewBannerView({
  openModal,
  storeLabel,
  supportEmail,
  onThumbsUp,
  onThumbsDown,
  onDismiss,
  onCloseModal,
  onOpenStore,
  onEmailSupport,
}: ViewProps) {
  return (
    <>
      {/* purple-100: a product ask, not a status alert, so it reads apart from
          the data cards (!important: Card hardcodes bg-white) */}
      <Card fullWidth className="box-border !bg-themed-purple-100">
        <div className="flex items-center gap-3 h-auto">
          <Text
            variant="body2"
            className="flex-1 !text-themed-grey-900 !font-semibold"
          >
            Do you love our app?
          </Text>
          <button
            aria-label="Yes, I love it"
            className={thumbClass}
            onClick={onThumbsUp}
          >
            <Icon name="ThumbsUp" size={20} />
          </button>
          <button
            aria-label="No, not really"
            className={thumbClass}
            onClick={onThumbsDown}
          >
            <Icon name="ThumbsDown" size={20} />
          </button>
          <button
            aria-label="Dismiss"
            className={thumbClass}
            onClick={onDismiss}
          >
            <Icon color="grey-500" name="X" size={20} />
          </button>
        </div>
      </Card>

      {openModal === 'up' && (
        <Modal
          id="review-banner-store-modal"
          open
          onClose={onCloseModal}
          maxWidth="20rem"
          title="Glad you're enjoying Optiwatt"
        >
          <div className="flex flex-col gap-4">
            <Text variant="body2">
              A quick review helps more people find us.
            </Text>
            <Button
              id="review-banner-store-button"
              variant="primary"
              onClick={onOpenStore}
            >
              {storeLabel}
            </Button>
            <Button
              id="review-banner-store-dismiss"
              variant="primary-subtle"
              onClick={onCloseModal}
            >
              Not now
            </Button>
          </div>
        </Modal>
      )}

      {openModal === 'down' && (
        <Modal
          id="review-banner-support-modal"
          open
          onClose={onCloseModal}
          maxWidth="20rem"
          title="Sorry to hear that"
        >
          <div className="flex flex-col gap-4">
            <Text variant="body2">
              Tell us what went wrong and we'll help. Email {supportEmail}.
            </Text>
            <Button
              id="review-banner-support-button"
              variant="primary"
              onClick={onEmailSupport}
            >
              Email support
            </Button>
            <Button
              id="review-banner-support-dismiss"
              variant="primary-subtle"
              onClick={onCloseModal}
            >
              Not now
            </Button>
          </div>
        </Modal>
      )}
    </>
  )
}

/**
 * Asks for an app store review at the top of the dashboard. Thumbs up offers the
 * store review, thumbs down routes to support so an unhappy person emails us
 * instead of leaving a one-star review. See useViewModel for who sees it.
 */
export default function ReviewBanner() {
  const viewModel = useViewModel()

  if (!viewModel.show) {
    return null
  }

  return <ReviewBannerView {...viewModel} />
}
